import PropTypes from "prop-types";
import { Mail, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { ContactActionMenu } from "@/components/people/ContactActionMenu";
import {
  avatarTone,
  channelBadgeTone,
  contactStatus,
  enrichPerson,
  fmtAgo,
  isActiveWithin,
} from "@/pages/people/contactMasterUtils";

export function ContactCard({
  person,
  inactiveIds,
  onViewHistory,
  onEdit,
  onToggleInactive,
  onDelete,
}) {
  const p = enrichPerson(person);
  const status = contactStatus(p, inactiveIds);
  const online = isActiveWithin(p.last_touch_at, 24);
  const convLabel = `${p.conversation_count || 0} convo${p.conversation_count === 1 ? "" : "s"}`;

  return (
    <article
      className="group relative flex h-full min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-[0_4px_20px_-6px_rgba(15,23,42,0.12)]"
      data-testid={`person-card-${p.id}`}
    >
      {p.is_blocked && (
        <div className="absolute left-2.5 top-2.5 z-10">
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200">
            Blocked
          </span>
        </div>
      )}

      <div className="absolute right-1.5 top-1.5 z-10 sm:right-2 sm:top-2">
        <ContactActionMenu
          status={status}
          onEdit={() => onEdit(p)}
          onToggleInactive={() => onToggleInactive(p)}
          onDelete={() => onDelete(p)}
        />
      </div>

      <div className="flex flex-col items-center pt-2 text-center">
        <div className="relative">
          <div
            className={cn(
              "flex h-14 w-14 items-center justify-center rounded-full text-[15px] font-semibold text-white",
              avatarTone(p.channel),
            )}
          >
            {p.initials || "??"}
          </div>
          <span
            className={cn(
              "absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full ring-2 ring-white",
              p.is_blocked ? "bg-rose-500" : online ? "bg-emerald-500" : "bg-slate-300",
            )}
            aria-hidden
            title={p.is_blocked ? "Blocked" : online ? "Active" : "Inactive"}
          />
        </div>
        <h3 className="mt-3 max-w-full truncate px-2 text-[14px] font-semibold text-slate-900">
          {p.name}
        </h3>
        <span
          className={cn(
            "mt-1 inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
            channelBadgeTone(p.channel),
          )}
        >
          {p.channel_label || p.channel}
        </span>
      </div>

      <div className="mt-4 space-y-1.5 px-1">
        <div className="flex items-start gap-2 min-w-0">
          <Mail size={13} className="mt-0.5 shrink-0 text-slate-400" />
          <span
            className={cn(
              "truncate text-[12px]",
              p.email ? "text-slate-600" : "text-slate-400",
            )}
            title={p.email || undefined}
          >
            {p.email || "No email on file"}
          </span>
        </div>
        <div className="flex items-start gap-2 min-w-0">
          <Phone size={13} className="mt-0.5 shrink-0 text-slate-400" />
          <span
            className={cn(
              "truncate text-[12px]",
              p.phone ? "text-slate-600" : "text-slate-400",
            )}
            title={p.phone || undefined}
          >
            {p.phone || "No phone on file"}
          </span>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100 pt-3 min-w-0">
        <span className="text-[11px] text-slate-500 truncate min-w-0">
          {convLabel} · {fmtAgo(p.last_touch_at)}
        </span>
        <button
          type="button"
          onClick={() => onViewHistory(p)}
          className="shrink-0 rounded-md border border-slate-200 px-2.5 py-2 sm:py-1 min-h-10 sm:min-h-0 text-[11.5px] font-medium text-slate-700 hover:border-indigo-200 hover:bg-indigo-50/50 hover:text-indigo-700 transition-colors"
          data-testid={`person-view-history-${p.id}`}
        >
          View History
        </button>
      </div>
    </article>
  );
}

ContactCard.propTypes = {
  person: PropTypes.object.isRequired,
  inactiveIds: PropTypes.instanceOf(Set),
  onViewHistory: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleInactive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
